<template>
  <div class="whatsapp-bot-tab w-full pb-12">
    <ConfirmDialog />

    <div v-if="!hasWhatsApp" class="needs-wa-banner">
      <div class="flex items-start gap-3">
        <i class="pi pi-whatsapp text-2xl text-green-500 mt-0.5"></i>
        <div class="flex-1">
          <div class="text-[15px] font-medium mb-1">דרוש חיבור של WhatsApp Business</div>
          <div class="text-xs text-surface-600 leading-relaxed mb-3">
            כדי להפעיל את הבוט החכם — לקבוע, להזיז ולבטל תורים, ולהריץ תהליכים מותאמים אישית —
            צריך קודם לחבר את חשבון ה-WhatsApp Business של העסק.
          </div>
          <Button
            label="חיבור WhatsApp Business"
            icon="pi pi-arrow-left"
            iconPos="right"
            size="small"
            class="!rounded-full"
            @click="openWhatsAppConnect"
          />
        </div>
      </div>
    </div>

    <div v-else-if="loading" class="flex justify-center items-center py-12">
      <loader />
    </div>

    <!-- Bot is connected but turned off -->
    <div v-else-if="!botEnabled" class="px-4 md:px-0">
      <div class="bot-off-card">
        <div class="off-icon"><i class="pi pi-comments"></i></div>
        <div class="text-lg font-medium mt-3">הבוט כבוי</div>
        <div class="text-sm text-surface-500 mt-1 leading-relaxed">
          כשהבוט פעיל הוא עונה ללקוחות ב-WhatsApp — קובע, מזיז ומבטל תורים ומריץ משימות מותאמות אישית.
          הפעילו אותו כדי להגדיר את המשימות שלו.
        </div>
        <Button
          label="הפעלת הבוט"
          icon="pi pi-power-off"
          :loading="toggling"
          class="!rounded-full mt-4"
          @click="enableBot"
        />
      </div>
    </div>

    <div v-else class="flex flex-col gap-3 w-full px-4 md:px-0">
      <div class="intro-banner">
        <div class="banner-circle" />
        <div class="banner-icon">
          <i class="pi pi-comments text-2xl"></i>
        </div>
        <div class="flex-1 relative">
          <div class="text-lg font-medium">הבוט של {{ businessTitle }} בעבודה</div>
          <div class="text-[13px] opacity-90 mt-1 leading-relaxed">
            הפעל/כבה את היכולות שיש לבוט בשיחות WhatsApp עם לקוחות, או הגדר משימות מותאמות אישית.
          </div>
        </div>
      </div>

      <!-- Built-in workflows -->
      <div class="section-title">משימות מובנות</div>
      <div class="cards-grid">
        <div
          v-for="wf in builtInWorkflows"
          :key="wf.type"
          class="workflow-card"
          @click="openEditDrawer(wf)"
        >
          <div class="flex items-start gap-3">
            <div
              class="icon-box"
              :style="{ backgroundColor: iconColorFor(wf) + '18', color: iconColorFor(wf) }"
            >
              <i :class="['pi', iconFor(wf)]"></i>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span class="font-medium text-[15px]">{{ wf.name }}</span>
                <div v-if="wf.enabled" class="live-dot" />
              </div>
              <div class="text-xs text-surface-500 mt-0.5 line-clamp-2">{{ wf.description }}</div>
            </div>
            <InputSwitch
              :modelValue="wf.enabled"
              @update:modelValue="handleToggle(wf, $event)"
              @click.stop
            />
          </div>
          <div class="card-footer">
            <i class="pi pi-wrench text-[12px] text-surface-500"></i>
            <span>{{ toolCountLabel(wf) }}</span>
            <div v-if="wf.type === 'cancel' || wf.type === 'reschedule'" class="footer-divider" />
            <button
              v-if="wf.type === 'cancel' || wf.type === 'reschedule'"
              class="policy-link"
              @click.stop="goToCancelPolicy"
            >
              מדיניות ביטולים
            </button>
          </div>
        </div>
      </div>

      <!-- Custom workflows -->
      <div class="section-header mt-4">
        <div class="section-title !m-0">משימות מותאמות אישית</div>
        <Button
          label="הוספה"
          icon="pi pi-plus"
          text
          size="small"
          class="!py-1"
          @click="openEditDrawer(blankCustomWorkflow())"
        />
      </div>
      <div v-if="customWorkflows.length === 0" class="empty-custom">
        <div class="empty-icon"><i class="pi pi-sparkles"></i></div>
        <div class="text-sm font-medium text-surface-700 mt-2 mb-1">עדיין אין משימות מותאמות</div>
        <div class="text-xs text-surface-500 leading-relaxed">
          משימות מותאמות מרחיבות את הבוט מעבר לקביעת תורים. הגדירו מתי הבוט ייכנס לפעולה ומה עליו לעשות — למשל:
        </div>
        <ul class="examples">
          <li>מתן הצעת מחיר משוערת לטיפול לפי תיאור הלקוח</li>
          <li>בקשת תמונות מהלקוח לפני התור</li>
          <li>מענה לשאלות נפוצות על העסק</li>
        </ul>
        <Button
          label="יצירת המשימה הראשונה"
          icon="pi pi-plus"
          size="small"
          class="!rounded-full mt-3"
          @click="openEditDrawer(blankCustomWorkflow())"
        />
      </div>
      <div v-else class="cards-grid">
        <div
          v-for="wf in customWorkflows"
          :key="wf.id"
          class="workflow-card"
          @click="openEditDrawer(wf)"
        >
          <div class="flex items-start gap-3">
            <div class="icon-box" style="background: #ede9fe; color: #6831d0;">
              <i class="pi pi-sparkles"></i>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span class="font-medium text-[15px] truncate">{{ wf.name }}</span>
                <div v-if="wf.enabled" class="live-dot" />
              </div>
              <div class="text-xs text-surface-500 mt-0.5 line-clamp-2">{{ wf.whenToTrigger }}</div>
            </div>
            <InputSwitch
              :modelValue="wf.enabled"
              @update:modelValue="handleToggle(wf, $event)"
              @click.stop
            />
          </div>
          <div class="card-footer">
            <i class="pi pi-wrench text-[12px] text-surface-500"></i>
            <span>{{ toolCountLabel(wf) }}</span>
            <div class="footer-divider" />
            <button class="policy-link text-red-500" @click.stop="confirmDelete(wf)">
              מחיקה
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { IAiWorkflow } from '@torim-app/types';
import { WorkflowTypeEnum } from '@torim-app/types';
import { computed, onMounted, ref } from 'vue';
import { useToast } from 'vue-toastification';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import { useAdminStore } from '@app/stores';
import { useDrawer } from '@torim-app/shared-components';
import { useRouter } from 'vue-router';
import { AdminRouteNames } from '@app/router/route-names';
import { AiWorkflowsApi, SettingsApi } from '@app/api/admin';
import { trackEvent } from '@app/composables/posthog';
import WhatsAppConnect from '@app/components/admin/pages/configurations/WhatsAppConnect.vue';
import EditWorkflowDrawer from './EditWorkflowDrawer.vue';
import { builtInMetaByType } from './workflow-types-config';

const adminStore = useAdminStore();
const toast = useToast();
const confirm = useConfirm();
const router = useRouter();
const { openDrawer } = useDrawer();

const loading = ref(true);
const toggling = ref(false);
const workflows = ref<IAiWorkflow[]>([]);

const hasWhatsApp = computed(() => !!adminStore.getBusinessSiteData?.whatsappIntegration?.accessToken);
const botEnabled = computed(() => adminStore.getBusinessSiteData?.whatsappIntegration?.aiEnabled ?? false);
const businessTitle = computed(() => adminStore.getBusinessSiteData?.title ?? 'העסק שלך');

const enableBot = async () => {
  trackEvent('whatsapp_bot_enabled_from_tab');
  toggling.value = true;
  try {
    await SettingsApi.toggleWhatsappAi(true);
    await adminStore.fetchBusinessData();
    if (!workflows.value.length) await loadWorkflows();
  } catch {
    toast.error('שגיאה בהפעלת הבוט');
  } finally {
    toggling.value = false;
  }
};

const builtInWorkflows = computed(() =>
  workflows.value.filter(w => w.type !== WorkflowTypeEnum.Custom),
);
const customWorkflows = computed(() =>
  workflows.value.filter(w => w.type === WorkflowTypeEnum.Custom),
);

const iconFor = (wf: IAiWorkflow) =>
  builtInMetaByType[wf.type]?.icon || 'pi-sparkles';
const iconColorFor = (wf: IAiWorkflow) =>
  builtInMetaByType[wf.type]?.color || '#6831d0';

const toolCountLabel = (wf: IAiWorkflow) => {
  const builtIn = builtInMetaByType[wf.type]?.toolsUsed.length ?? 0;
  const custom = wf.enabledTools?.length ?? 0;
  const total = builtIn + custom;
  return total === 0 ? 'תגובה בטקסט בלבד' : `${total} כלים`;
};

const loadWorkflows = async () => {
  loading.value = true;
  try {
    workflows.value = await AiWorkflowsApi.list();
  } catch (e) {
    toast.error('טעינת המשימות נכשלה');
    console.error(e);
  } finally {
    loading.value = false;
  }
};

const handleToggle = async (wf: IAiWorkflow, enabled: boolean) => {
  trackEvent('whatsapp_bot_workflow_toggled', { type: wf.type, enabled });
  const updated = { ...wf, enabled };
  try {
    const saved = await AiWorkflowsApi.upsert(updated);
    const idx = workflows.value.findIndex(w =>
      w.type === WorkflowTypeEnum.Custom ? w.id === saved.id : w.type === saved.type,
    );
    if (idx >= 0) workflows.value[idx] = saved;
    else workflows.value.push(saved);
  } catch {
    toast.error('שמירת השינוי נכשלה');
  }
};

const openEditDrawer = (wf: IAiWorkflow) => {
  trackEvent('whatsapp_bot_workflow_opened', { type: wf.type, isCustom: wf.type === WorkflowTypeEnum.Custom });
  openDrawer(
    EditWorkflowDrawer,
    {
      workflow: { ...wf, enabledTools: wf.enabledTools ? [...wf.enabledTools] : [] },
      onSave: async (saved: IAiWorkflow) => {
        try {
          const result = await AiWorkflowsApi.upsert(saved);
          const idx = workflows.value.findIndex(w =>
            w.type === WorkflowTypeEnum.Custom ? w.id === result.id : w.type === result.type,
          );
          if (idx >= 0) workflows.value[idx] = result;
          else workflows.value.push(result);
          toast.success('המשימה נשמרה');
        } catch {
          toast.error('שמירת המשימה נכשלה');
        }
      },
    },
    { title: wf.id || wf.type !== WorkflowTypeEnum.Custom ? wf.name : 'משימה חדשה' },
  );
};

const blankCustomWorkflow = (): IAiWorkflow => ({
  type: WorkflowTypeEnum.Custom,
  enabled: true,
  name: '',
  description: '',
  whenToTrigger: '',
  instructions: '',
  definitionOfDone: '',
  enabledTools: [],
  notifyOwnerOnDone: true,
});

const confirmDelete = (wf: IAiWorkflow) => {
  if (!wf.id) return;
  confirm.require({
    message: `למחוק את המשימה "${wf.name}"?`,
    header: 'מחיקת משימה',
    rejectLabel: 'לא',
    acceptLabel: 'כן, מחק',
    accept: async () => {
      try {
        await AiWorkflowsApi.remove(wf.id!);
        workflows.value = workflows.value.filter(w => w.id !== wf.id);
        toast.success('המשימה נמחקה');
      } catch {
        toast.error('מחיקת המשימה נכשלה');
      }
    },
  });
};

const goToCancelPolicy = () => {
  router.push({ name: AdminRouteNames.Configurations, query: { tab: '1' } });
};

const openWhatsAppConnect = () => {
  openDrawer(WhatsAppConnect, {}, { title: 'WhatsApp Business' });
};

onMounted(() => {
  trackEvent('whatsapp_bot_tab_viewed');
  if (hasWhatsApp.value) loadWorkflows();
  else loading.value = false;
});
</script>

<style scoped lang="scss">
.whatsapp-bot-tab {
  .needs-wa-banner {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 14px;
    padding: 18px;
    margin: 16px;
  }

  .intro-banner {
    background: linear-gradient(135deg, #16a34a 0%, #0e7e36 100%);
    border-radius: 14px;
    padding: 22px 26px;
    margin-bottom: 12px;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 22px;
    position: relative;
    overflow: hidden;
  }

  .banner-circle {
    position: absolute;
    left: -20px;
    top: -20px;
    width: 180px;
    height: 180px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
  }

  .banner-icon {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.18);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .section-title {
    font-size: 12px;
    font-weight: 600;
    color: var(--p-surface-600);
    letter-spacing: 0.4px;
    text-transform: uppercase;
    margin: 8px 0 4px;
  }

  .cards-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .workflow-card {
    background: var(--p-surface-0);
    border: 1px solid var(--p-surface-200);
    border-radius: 12px;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    cursor: pointer;
    transition: box-shadow 0.15s, border-color 0.15s;

    &:hover {
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    }
  }

  .icon-box {
    width: 2.625rem;
    height: 2.625rem;
    border-radius: 0.625rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    i {
      font-size: 1.25rem;
    }
  }

  .live-dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: #15803d;
  }

  .card-footer {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 10px;
    border-top: 1px solid var(--p-surface-100);
    font-size: 12px;
    color: var(--p-surface-600);
  }

  .footer-divider {
    width: 1px;
    height: 12px;
    background: var(--p-surface-200);
  }

  .policy-link {
    color: var(--p-primary-600);
    background: none;
    border: none;
    padding: 0;
    font-size: 12px;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }

  .empty-custom {
    background: var(--p-surface-50);
    border: 1px dashed var(--p-surface-300);
    border-radius: 12px;
    padding: 18px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .bot-off-card {
    background: var(--p-surface-0);
    border: 1px solid var(--p-surface-200);
    border-radius: 14px;
    padding: 28px 22px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .off-icon {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: var(--p-surface-100);
    color: var(--p-surface-400);
    display: flex;
    align-items: center;
    justify-content: center;
    i {
      font-size: 1.6rem;
    }
  }

  .empty-icon {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    background: #ede9fe;
    color: #6831d0;
    display: flex;
    align-items: center;
    justify-content: center;
    i {
      font-size: 1.15rem;
    }
  }

  .examples {
    list-style: disc;
    padding-inline-start: 18px;
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 12px;
    color: var(--p-surface-500);
  }
}
</style>
